/**
 * ImageGuard
 * ─────────────────────────────────────────────────────────────────────────
 * Site-wide "no easy download" net for vendor artwork. Mounted ONCE in
 * App.jsx and never rendered anywhere else.
 *
 * What it stops: the casual saver. Right-click "Save image as…", dragging a
 * picture to the desktop or another tab, and the iOS / Android long-press
 * "Save image" sheet. Every one of those goes through the image element
 * itself, so blocking them on the element is enough and nothing else on the
 * page changes.
 *
 * What it does NOT stop, by design: DevTools, the Network tab and
 * screenshots. Those are the Cloudinary lockdown's job (private delivery
 * type + strict transformations), which is a separate pass. This file is
 * the cheap layer that costs zero quality, zero speed and zero uptime.
 *
 * Scope: only images served from Cloudinary (every vendor upload) plus any
 * element that opts in with `data-protected-image`. Deliberately NOT every
 * <img> on the site: QR codes, receipts and the Lusuku logo stay saveable,
 * and the share buttons' right-click-for-WhatsApp handlers keep working
 * because they are buttons, not images.
 *
 * Why document-level listeners instead of props on ~40 components: the
 * storefronts render Cloudinary art through styled-components `img`
 * selectors, MUI carousels, background-image zoom panes and a jQuery
 * magnifier. Two capturing listeners cover all of them at once, and a new
 * surface is protected the day it ships without anyone remembering to add
 * a prop. `ProtectedImage` exists for the handful of high-value surfaces
 * (lightboxes, zoom stages) as belt and braces on top of this.
 */
import { useEffect } from "react";
import { createGlobalStyle } from "styled-components";

export const CLOUDINARY_HOST = "res.cloudinary.com";
export const PROTECTED_ATTR = "data-protected-image";

const CLOUDINARY_IMG_SELECTOR = `img[src*="${CLOUDINARY_HOST}"]`;

/**
 * True when an event target is (or sits inside) vendor artwork we guard.
 * Exported for tests. `closest` is guarded because text nodes and the
 * document itself have no element API.
 */
export const isGuardedImageTarget = (target) => {
  if (!target || typeof target.closest !== "function") return false;
  if (target.closest(`[${PROTECTED_ATTR}]`)) return true;
  if (target.tagName === "IMG") {
    const src = target.currentSrc || target.src || "";
    return src.includes(CLOUDINARY_HOST);
  }
  // <picture> sources and the magnify plugin's lens wrap the img; walk up
  // one level so a click landing on the wrapper still counts.
  const img = target.closest("picture, .magnify");
  if (img) {
    const inner = img.querySelector("img");
    const src = (inner && (inner.currentSrc || inner.src)) || "";
    return src.includes(CLOUDINARY_HOST);
  }
  return false;
};

/**
 * Install the two listeners. Returns the uninstaller. Kept separate from the
 * component so it can be unit-tested with a bare document.
 */
export const installImageGuard = (doc = document) => {
  const block = (event) => {
    if (isGuardedImageTarget(event.target)) event.preventDefault();
  };
  // Capture phase so a component's own stopPropagation never lets a menu
  // through by accident.
  doc.addEventListener("contextmenu", block, true);
  doc.addEventListener("dragstart", block, true);
  return () => {
    doc.removeEventListener("contextmenu", block, true);
    doc.removeEventListener("dragstart", block, true);
  };
};

/* The CSS half. `-webkit-touch-callout: none` is the only thing that stops
   the iOS long-press save sheet; `user-drag` stops the drag ghost in
   WebKit/Blink before the dragstart listener is even consulted; user-select
   stops the blue selection highlight that a frustrated right-clicker leaves
   behind. None of these change layout, paint or loading. */
const GuardStyles = createGlobalStyle`
  ${CLOUDINARY_IMG_SELECTOR},
  [${PROTECTED_ATTR}],
  [${PROTECTED_ATTR}] img {
    -webkit-user-drag: none;
    user-drag: none;
    -webkit-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }
`;

const ImageGuard = () => {
  useEffect(() => installImageGuard(document), []);
  return <GuardStyles />;
};

export default ImageGuard;
